import { useState, type FormEvent } from "react";
import { ArrowRight, Lock, ShieldCheck } from "lucide-react";

export function LoginScreen({ onSuccess }: { onSuccess: () => void | Promise<void> }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = (await res.json()) as { error?: string };
      if (!res.ok) {
        setError(data.error ?? "Não foi possível entrar");
        return;
      }
      await onSuccess();
    } catch {
      setError("Erro de rede");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cb-login">
      <aside className="cb-login__aside cb-fade">
        <span className="cb-brand" style={{ fontSize: "1.6rem" }}>
          CHECK<span style={{ color: "var(--cb-accent)" }}>BUN</span>
        </span>

        <div className="cb-rise-1" style={{ display: "grid", gap: "1.1rem", maxWidth: "30rem" }}>
          <p className="cb-display cb-hero-title" style={{ margin: 0 }}>
            Check-in
            <br />
            no segundo
            <br />
            <span style={{ color: "var(--cb-accent)" }}>em que abre.</span>
          </p>
          <p style={{ color: "var(--cb-muted)", margin: 0, maxWidth: "26rem" }}>
            O agendador consulta a agenda da academia, prepara o{" "}
            <span className="cb-num" style={{ color: "var(--cb-text)" }}>idConfiguracao</span> da aula e dispara o
            check-in exatamente 12 horas antes — com retry até conseguir vaga.
          </p>
        </div>

        <div className="cb-rise-2" style={{ display: "grid", gap: "0.5rem" }}>
          <div className="cb-kv" style={{ borderStyle: "solid" }}>
            <span className="cb-kv__k">janela</span>
            <span className="cb-kv__v">aula − 12h</span>
          </div>
          <div className="cb-kv" style={{ borderStyle: "solid" }}>
            <span className="cb-kv__k">retry</span>
            <span className="cb-kv__v">200ms → 1.2s com jitter</span>
          </div>
          <div className="cb-kv" style={{ borderStyle: "solid" }}>
            <span className="cb-kv__k">timeout</span>
            <span className="cb-kv__v">300s por ocorrência</span>
          </div>
        </div>
      </aside>

      <div className="cb-login__card">
        <form onSubmit={handleSubmit} className="cb-rise" style={{ display: "grid", gap: "1.5rem" }}>
          <div style={{ display: "grid", gap: "0.4rem" }}>
            <span className="cb-brand" style={{ fontSize: "1.4rem" }}>
              Entrar
            </span>
            <p className="cb-label" style={{ textTransform: "none", letterSpacing: "0.02em" }}>
              use o mesmo email e senha do app da academia
            </p>
          </div>

          <div style={{ display: "grid", gap: "0.9rem" }}>
            <label style={{ display: "grid", gap: "0.35rem" }}>
              <span className="cb-label">Email</span>
              <input
                className="cb-input"
                type="email"
                autoComplete="username"
                placeholder="voce@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>

            <label style={{ display: "grid", gap: "0.35rem" }}>
              <span className="cb-label">Senha</span>
              <div style={{ position: "relative" }}>
                <input
                  className="cb-input"
                  type="password"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  style={{ paddingRight: "2.4rem" }}
                />
                <Lock
                  size={14}
                  style={{ position: "absolute", right: "0.8rem", top: "50%", transform: "translateY(-50%)", color: "var(--cb-subtle)" }}
                />
              </div>
            </label>
          </div>

          {error && (
            <p
              className="cb-fade"
              style={{
                margin: 0,
                padding: "0.65rem 0.8rem",
                borderRadius: "0.55rem",
                border: "1px solid rgba(255,68,55,.3)",
                background: "var(--cb-error-dim)",
                color: "var(--cb-error)",
                fontFamily: "var(--font-mono)",
                fontSize: "0.75rem",
                lineHeight: 1.5,
              }}
            >
              {error}
            </p>
          )}

          <button type="submit" className="cb-btn cb-btn--primary" disabled={loading} style={{ padding: "0.8rem" }}>
            {loading ? "Entrando…" : "Entrar"}
            <ArrowRight size={15} />
          </button>

          <p className="cb-label" style={{ display: "flex", alignItems: "center", gap: "0.4rem", textTransform: "none", letterSpacing: "0.02em" }}>
            <ShieldCheck size={13} style={{ color: "var(--cb-success)" }} />
            a senha fica cifrada e só é usada para autenticar na Fiti
          </p>
        </form>
      </div>
    </div>
  );
}
